import { BadRequestException, Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import {
  CartLine, ChannelConfig, KINDS_BY_CHANNEL, SHIPPING_KINDS, ShippingChannelName,
  ShippingKind, ShippingQuote, ShippingZoneKind, quoteShipping, resolveChannel,
  haversineKm, resolveZone, roadDistanceKm,
} from '@cart/shared-utils';

/**
 * How far a supplied coordinate may sit from its pincode centroid.
 *
 * Generous, because a rural pincode really is tens of kilometres across; tight
 * enough that a coordinate from a different city is rejected. This is the check
 * that stops client-supplied coordinates being a way to choose a cheaper price.
 */
const COORDINATE_TRUST_KM = 25;

/** What a caller must tell us to get a price. */
export interface QuoteRequest {
  destination: {
    postalCode?: string | null;
    city?: string | null;
    state?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
  lines: CartLine[];
  subtotalPaise: number;
  cod?: boolean;
  freeShipping?: boolean;
}

export interface QuoteResult extends ShippingQuote {
  channel: ShippingChannelName;
  kind: ShippingKind;
  /** The merchant's name for this method, shown to the shopper. */
  methodName: string;
  zone: ShippingZoneKind | null;
  distanceKm: number | null;
  pickupAvailable: boolean;
  pickupReadyMinutes: number | null;
}

/**
 * Shipping, resolved server-side.
 *
 * The single most important property of this service is what it does *not*
 * accept: a price, or a rate id chosen by the browser. The previous
 * implementation looked up whatever `shippingRateId` the client sent and
 * charged it, which let a shopper post the cheapest rate with a Guwahati
 * address. Here the address decides the channel, the channel decides the one
 * method, and the method decides the price — none of it reachable from the
 * request body.
 */
@Injectable()
export class ShippingService {
  constructor(private readonly prisma: PrismaService) {}

  // ─── Configuration ─────────────────────────────────────────────────────

  /** Settings, creating the row with defaults the first time it is asked for. */
  async getSettings(storeId: string) {
    return (
      (await this.prisma.shippingSettings.findUnique({ where: { storeId } })) ??
      this.prisma.shippingSettings.create({ data: { storeId } })
    );
  }

  /**
   * Settings plus the shop's coordinates, for the admin form.
   *
   * The coordinates are **not** a shipping column. They live on
   * `store_branding`, where the storefront listing and the schema.org
   * `GeoCircle` already read them from, and this exposes that one row through a
   * second editing surface rather than keeping a second copy. Two places to
   * store where a shop is would be two places for them to disagree — and the
   * disagreement would show as a delivery radius drawn around the wrong point.
   */
  async getSettingsForAdmin(storeId: string) {
    const [settings, branding] = await Promise.all([
      this.getSettings(storeId),
      this.prisma.storeBranding.findUnique({
        where: { storeId },
        select: { latitude: true, longitude: true },
      }),
    ]);

    return {
      ...settings,
      originLatitude: branding?.latitude != null ? Number(branding.latitude) : null,
      originLongitude: branding?.longitude != null ? Number(branding.longitude) : null,
    };
  }

  async saveSettings(storeId: string, data: Record<string, unknown>, actorId: string) {
    const clean = {
      localBoundary: this.oneOf(data['localBoundary'], ['radius', 'same_city', 'same_state']),
      localRadiusKm: this.optionalInt(data['localRadiusKm'], 1, 500),
      originPincode: this.optionalPincode(data['originPincode']),
      pickupEnabled: data['pickupEnabled'] === undefined ? undefined : !!data['pickupEnabled'],
      pickupReadyMinutes: this.optionalInt(data['pickupReadyMinutes'], 0, 10080),
      defaultWeightGrams: this.optionalInt(data['defaultWeightGrams'], 1, 100000),
    };

    // Undefined means "not submitted"; null means "cleared". Prisma treats them
    // differently and conflating them would wipe fields a form never showed.
    const update = Object.fromEntries(
      Object.entries(clean).filter(([, value]) => value !== undefined)
    );

    // Coordinates are written back to store_branding — see getSettingsForAdmin
    // for why they do not live on the shipping row.
    const lat = this.optionalCoordinate(data['originLatitude'], 6, 38, 'latitude');
    const lng = this.optionalCoordinate(data['originLongitude'], 66, 98, 'longitude');

    if (lat !== undefined || lng !== undefined) {
      if ((lat ?? null) === null !== ((lng ?? null) === null)) {
        // Half a pair puts the shop in the sea, and every distance measured
        // from it would be nonsense rather than merely wrong.
        throw new BadRequestException('Enter both latitude and longitude, or neither');
      }
      const coords = { latitude: lat ?? null, longitude: lng ?? null };
      await this.prisma.storeBranding.upsert({
        where: { storeId },
        create: { storeId, ...coords, createdBy: actorId, updatedBy: actorId },
        update: { ...coords, updatedBy: actorId },
      });
    }

    await this.prisma.shippingSettings.upsert({
      where: { storeId },
      create: { storeId, ...update, createdBy: actorId, updatedBy: actorId },
      update: { ...update, updatedBy: actorId },
    });

    return this.getSettingsForAdmin(storeId);
  }

  /**
   * A coordinate inside India's bounding box, or nothing.
   *
   * Bounds rather than a bare number check: a transposed pair (longitude typed
   * into the latitude field) lands outside India and is caught here, where a
   * range check on -90..90 would wave it through and put the shop off the coast
   * of Somalia.
   */
  private optionalCoordinate(value: unknown, min: number, max: number, label: string): number | null | undefined {
    if (value === undefined) return undefined;
    if (value === null || value === '') return null;

    const n = Number(value);
    if (!Number.isFinite(n)) throw new BadRequestException(`Expected a number for ${label}`);
    if (n < min || n > max) {
      throw new BadRequestException(`That ${label} is outside India — check the two are not swapped`);
    }
    return n;
  }

  /** Every channel a store has configured, with its method's own rows. */
  async listChannels(storeId: string) {
    return this.prisma.shippingChannel.findMany({
      where: { storeId },
      include: {
        bands: { orderBy: { uptoKm: 'asc' } },
        zoneRates: true,
        valueSlabs: { orderBy: { uptoPaise: 'asc' } },
        weightSlabs: { orderBy: { uptoGrams: 'asc' } },
      },
      orderBy: { channel: 'asc' },
    });
  }

  /**
   * Configure one channel.
   *
   * Child rows are replaced wholesale for the kind being saved, and rows
   * belonging to *other* kinds are left alone — so a merchant who tries
   * `zone_weight`, dislikes it and switches back to `flat` has not lost the
   * rate card they typed in.
   */
  async saveChannel(
    storeId: string,
    channel: ShippingChannelName,
    body: Record<string, unknown>,
    actorId: string
  ) {
    const kind = String(body['kind'] ?? '') as ShippingKind;
    if (!SHIPPING_KINDS.includes(kind)) {
      throw new BadRequestException(`Unknown shipping method "${kind}"`);
    }
    if (!KINDS_BY_CHANNEL[channel]?.includes(kind)) {
      throw new BadRequestException(
        `"${kind}" cannot price the ${channel} channel — see the methods table in doc 25 §11.2`
      );
    }

    const data = {
      kind,
      name: String(body['name'] ?? '').trim() || 'Delivery',
      basePaise: this.optionalInt(body['basePaise'], 0, 100_000_00) ?? null,
      freeAbovePaise: this.optionalInt(body['freeAbovePaise'], 0, 10_000_000_00) ?? null,
      codEnabled: body['codEnabled'] === undefined ? true : !!body['codEnabled'],
      codFlatPaise: this.optionalInt(body['codFlatPaise'], 0, 100_000_00) ?? 0,
      codPercentBps: this.optionalInt(body['codPercentBps'], 0, 10000) ?? 0,
      handlingPaise: this.optionalInt(body['handlingPaise'], 0, 100_000_00) ?? 0,
      isActive: body['isActive'] === undefined ? true : !!body['isActive'],
    };

    const saved = await this.prisma.shippingChannel.upsert({
      where: { storeId_channel: { storeId, channel } },
      create: { storeId, channel, ...data, createdBy: actorId, updatedBy: actorId },
      update: { ...data, updatedBy: actorId },
    });

    await this.replaceChildRows(saved.id, kind, body);
    return this.prisma.shippingChannel.findUnique({
      where: { id: saved.id },
      include: { bands: true, zoneRates: true, valueSlabs: true, weightSlabs: true },
    });
  }

  /**
   * Throw the whole configuration away and start again.
   *
   * Deliberately re-seeds rather than leaving the store with nothing: a store
   * with no channel at all cannot take an order, so "reset" that emptied the
   * table would break checkout rather than clearing a mistake. What a merchant
   * means by reset is "put it back how it arrived".
   */
  async resetAll(storeId: string, actorId: string) {
    await this.prisma.shippingChannel.deleteMany({ where: { storeId } });
    await this.prisma.shippingSettings.deleteMany({ where: { storeId } });

    await this.prisma.shippingChannel.create({
      data: {
        storeId,
        channel: 'outstation',
        kind: 'flat',
        name: 'Standard delivery',
        basePaise: 5000,
        freeAbovePaise: 49900,
        createdBy: actorId,
        updatedBy: actorId,
      },
    });

    return {
      settings: await this.getSettingsForAdmin(storeId),
      channels: await this.listChannels(storeId),
    };
  }

  /** Turning a channel off is how a merchant says "I don't deliver there". */
  async deleteChannel(storeId: string, channel: ShippingChannelName) {
    await this.prisma.shippingChannel.deleteMany({ where: { storeId, channel } });
    return { channel, removed: true };
  }

  private async replaceChildRows(channelId: string, kind: ShippingKind, body: Record<string, unknown>) {
    const rows = (key: string) => (Array.isArray(body[key]) ? (body[key] as Record<string, unknown>[]) : []);

    if (kind === 'distance') {
      await this.prisma.shippingDistanceBand.deleteMany({ where: { channelId } });
      const bands = rows('bands')
        .map(b => ({
          channelId,
          uptoKm: this.requireInt(b['uptoKm'], 1, 500, 'uptoKm'),
          basePaise: this.requireInt(b['basePaise'], 0, 100_000_00, 'basePaise'),
          perKmPaise: this.optionalInt(b['perKmPaise'], 0, 100_00) ?? 0,
          minMinutes: this.optionalInt(b['minMinutes'], 0, 10080) ?? null,
          maxMinutes: this.optionalInt(b['maxMinutes'], 0, 10080) ?? null,
        }))
        .sort((a, b) => a.uptoKm - b.uptoKm);

      this.assertAscending(bands.map(b => b.uptoKm), 'delivery bands');
      if (bands.length) await this.prisma.shippingDistanceBand.createMany({ data: bands });
      return;
    }

    if (kind === 'zone_weight') {
      await this.prisma.shippingZoneRate.deleteMany({ where: { channelId } });
      const rates = rows('zoneRates').map(r => ({
        channelId,
        zone: String(r['zone'] ?? ''),
        basePaise: this.requireInt(r['basePaise'], 0, 100_000_00, 'basePaise'),
        firstSlabGrams: this.optionalInt(r['firstSlabGrams'], 1, 100000) ?? 500,
        perSlabPaise: this.optionalInt(r['perSlabPaise'], 0, 100_000_00) ?? 0,
        slabGrams: this.optionalInt(r['slabGrams'], 1, 100000) ?? 500,
        minDays: this.optionalInt(r['minDays'], 0, 90) ?? null,
        maxDays: this.optionalInt(r['maxDays'], 0, 90) ?? null,
      }));
      if (rates.length) await this.prisma.shippingZoneRate.createMany({ data: rates });
      return;
    }

    if (kind === 'order_value') {
      await this.prisma.shippingValueSlab.deleteMany({ where: { channelId } });
      const slabs = rows('valueSlabs')
        .map(s => ({
          channelId,
          uptoPaise: this.requireInt(s['uptoPaise'], 0, 10_000_000_00, 'uptoPaise'),
          pricePaise: this.requireInt(s['pricePaise'], 0, 100_000_00, 'pricePaise'),
        }))
        .sort((a, b) => a.uptoPaise - b.uptoPaise);

      this.assertAscending(slabs.map(s => s.uptoPaise), 'order-value slabs');
      if (slabs.length) await this.prisma.shippingValueSlab.createMany({ data: slabs });
      return;
    }

    if (kind === 'weight') {
      await this.prisma.shippingWeightSlab.deleteMany({ where: { channelId } });
      const slabs = rows('weightSlabs')
        .map(s => ({
          channelId,
          uptoGrams: this.requireInt(s['uptoGrams'], 1, 1_000_000, 'uptoGrams'),
          pricePaise: this.requireInt(s['pricePaise'], 0, 100_000_00, 'pricePaise'),
        }))
        .sort((a, b) => a.uptoGrams - b.uptoGrams);

      this.assertAscending(slabs.map(s => s.uptoGrams), 'weight slabs');
      if (slabs.length) await this.prisma.shippingWeightSlab.createMany({ data: slabs });
    }
  }

  // ─── Quoting ───────────────────────────────────────────────────────────

  /**
   * What this cart costs to deliver to this address.
   *
   * The one entry point for checkout, the storefront and the admin simulator,
   * so all three answer identically. A quote that differed between the price a
   * shopper was shown and the price they were charged would be worse than
   * either being wrong on its own.
   */
  async quote(storeId: string, request: QuoteRequest): Promise<QuoteResult> {
    const [settings, channels, store] = await Promise.all([
      this.getSettings(storeId),
      this.listChannels(storeId),
      this.prisma.store.findUnique({
        where: { id: storeId },
        select: { city: true, state: true, branding: { select: { latitude: true, longitude: true } } },
      }),
    ]);

    const origin = store?.branding?.latitude != null && store.branding.longitude != null
      ? { latitude: Number(store.branding.latitude), longitude: Number(store.branding.longitude) }
      : null;

    const destinationCoords = await this.destinationCoordinates(request.destination);
    const distanceKm = origin && destinationCoords
      ? roadDistanceKm(origin, destinationCoords)
      : null;

    const channelName = resolveChannel({
      boundary: settings.localBoundary as 'radius' | 'same_city' | 'same_state',
      localRadiusKm: settings.localRadiusKm,
      distanceKm,
      originCity: store?.city,
      originState: store?.state,
      destCity: request.destination.city,
      destState: request.destination.state,
    });

    const zone = resolveZone({
      originPincode: settings.originPincode,
      originCity: store?.city,
      originState: store?.state,
      destPincode: request.destination.postalCode,
      destCity: request.destination.city,
      destState: request.destination.state,
    });

    const row = channels.find(c => c.channel === channelName && c.isActive);
    const pickup = {
      pickupAvailable: settings.pickupEnabled,
      pickupReadyMinutes: settings.pickupReadyMinutes,
    };

    if (!row) {
      // An unconfigured channel is a merchant saying "I don't deliver there".
      // Said plainly, with the reason, rather than failing the checkout.
      return {
        deliverable: false, amountPaise: 0,
        reasons: [`This store does not deliver to ${channelName === 'local' ? 'nearby addresses' : 'this area'} yet`],
        codAvailable: false, codFeePaise: 0,
        channel: channelName, kind: 'flat', methodName: 'Delivery',
        zone, distanceKm, ...pickup,
      };
    }

    const config: ChannelConfig = {
      channel: channelName,
      kind: row.kind as ShippingKind,
      name: row.name,
      basePaise: row.basePaise,
      bands: row.bands,
      zoneRates: row.zoneRates.map(r => ({ ...r, zone: r.zone as ShippingZoneKind })),
      valueSlabs: row.valueSlabs,
      weightSlabs: row.weightSlabs,
      freeAbovePaise: row.freeAbovePaise,
      codEnabled: row.codEnabled,
      codFlatPaise: row.codFlatPaise,
      codPercentBps: row.codPercentBps,
      handlingPaise: row.handlingPaise,
    };

    const quote = quoteShipping(config, {
      lines: request.lines,
      subtotalPaise: request.subtotalPaise,
      defaultWeightGrams: settings.defaultWeightGrams,
      zone,
      distanceKm,
      cod: request.cod,
      freeShipping: request.freeShipping,
    });

    return {
      ...quote,
      channel: channelName,
      kind: row.kind as ShippingKind,
      methodName: row.name,
      zone,
      distanceKm,
      ...pickup,
    };
  }

  /**
   * Where to measure the delivery to.
   *
   * The pincode centroid is the anchor and the always-available answer. Client
   * coordinates — from a map pin or a granted geolocation — are a *refinement*
   * of it, never a replacement: a shopper who could post `{lat, lng}` and have
   * the server price from it would be setting their own delivery charge by
   * editing two numbers.
   *
   * So supplied coordinates are only honoured when they agree with the address
   * the order is actually going to. Beyond {@link COORDINATE_TRUST_KM} of the
   * pincode centroid they are discarded and the centroid is used instead, which
   * is the same answer the order would have had without them.
   */
  private async destinationCoordinates(destination: QuoteRequest['destination']) {
    const centroid = await this.pincodeCentroid(destination.postalCode);
    const supplied = this.plausibleCoordinates(destination);

    if (!supplied) return centroid;
    if (!centroid) {
      // Nothing to check against. An unverifiable coordinate cannot be allowed
      // to set a price, so the order falls through to zone pricing.
      return null;
    }

    return haversineKm(centroid, supplied) <= COORDINATE_TRUST_KM ? supplied : centroid;
  }

  /** The centroid for a pincode, or null when it is not one we hold. */
  private async pincodeCentroid(postalCode: string | null | undefined) {
    const pincode = (postalCode ?? '').trim();
    if (!/^[1-9]\d{5}$/.test(pincode)) return null;

    const row = await this.prisma.pincodeGeo.findUnique({
      where: { pincode },
      select: { latitude: true, longitude: true },
    });

    return row ? { latitude: Number(row.latitude), longitude: Number(row.longitude) } : null;
  }

  /** Inside India's bounding box, and actually numbers. */
  private plausibleCoordinates(destination: QuoteRequest['destination']) {
    const { latitude, longitude } = destination;
    if (latitude == null || longitude == null) return null;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

    const inIndia = latitude >= 6 && latitude <= 38 && longitude >= 66 && longitude <= 98;
    return inIndia ? { latitude, longitude } : null;
  }

  /**
   * Serviceability and city/state for a pincode, for the checkout form.
   *
   * Autofilling city and state from a pincode is something every Indian
   * shopper now expects, and it comes free with the table distance pricing
   * already needs.
   */
  async lookupPincode(pincode: string) {
    const row = await this.prisma.pincodeGeo.findUnique({
      where: { pincode: (pincode ?? '').trim() },
      select: { district: true, state: true, isMetro: true, isOda: true },
    });

    return row
      ? { known: true, city: row.district, state: row.state, isMetro: row.isMetro, isOda: row.isOda }
      : { known: false };
  }

  // ─── Validation helpers ────────────────────────────────────────────────

  private oneOf(value: unknown, allowed: string[]): string | undefined {
    if (value === undefined) return undefined;
    const text = String(value);
    if (!allowed.includes(text)) {
      throw new BadRequestException(`Expected one of ${allowed.join(', ')}`);
    }
    return text;
  }

  private optionalInt(value: unknown, min: number, max: number): number | undefined {
    if (value === undefined) return undefined;
    if (value === null || value === '') return undefined;

    const n = Number(value);
    if (!Number.isFinite(n)) throw new BadRequestException('Expected a number');

    const rounded = Math.round(n);
    if (rounded < min || rounded > max) {
      throw new BadRequestException(`Value must be between ${min} and ${max}`);
    }
    return rounded;
  }

  private requireInt(value: unknown, min: number, max: number, field: string): number {
    const n = this.optionalInt(value, min, max);
    if (n === undefined) throw new BadRequestException(`${field} is required`);
    return n;
  }

  private optionalPincode(value: unknown): string | undefined {
    if (value === undefined) return undefined;
    const text = String(value ?? '').trim();
    if (!text) return undefined;
    if (!/^\d{6}$/.test(text)) throw new BadRequestException('A pincode is six digits');
    return text;
  }

  /**
   * Slabs and bands must step upward with no repeats.
   *
   * Two slabs with the same ceiling is not a harmless duplicate: pricing picks
   * the first match, so the second is dead configuration a merchant believes is
   * live — which they discover from a customer's bill.
   */
  private assertAscending(values: number[], label: string): void {
    for (let i = 1; i < values.length; i++) {
      if (values[i] <= values[i - 1]) {
        throw new BadRequestException(`Each of the ${label} must be larger than the one before it`);
      }
    }
  }
}
